import React from "react";
import { useTranslate } from "@refinedev/core";
import { Alert } from "antd";
import { useOpeningStatus } from "../../hooks/tenant";
import type { TenantWeekday } from "../../models/tenant";

interface OpeningHoursNoticeProps {
    className?: string;
}

/**
 * Highlighted warning shown on the order creation page while the tenant is
 * closed. Informational only — the customer can still submit, the order is
 * simply picked up at reopening.
 */
export const OpeningHoursNotice: React.FC<OpeningHoursNoticeProps> = ({ className }) => {
    const _t = useTranslate();
    const { status } = useOpeningStatus();

    if (!status || status.isOpen) {
        return null;
    }

    const dayName = (day: TenantWeekday) => _t(`openingHours.weekdays.${day}`);

    const reopening = status.nextOpening
        ? status.nextOpening.isToday
            ? _t("openingHours.orderNotice.reopensToday", {
                  time: status.nextOpening.time,
              })
            : _t("openingHours.orderNotice.reopensOn", {
                  day: dayName(status.nextOpening.weekday),
                  time: status.nextOpening.time,
              })
        : null;

    const title =
        status.reason === "holiday" && status.activeClosure?.label
            ? _t("openingHours.orderNotice.holidayTitle", {
                  label: status.activeClosure.label,
              })
            : _t("openingHours.orderNotice.title");

    return (
        <Alert
            type="warning"
            showIcon
            className={className}
            message={title}
            description={
                <span>
                    {_t("openingHours.orderNotice.description")}
                    {reopening ? ` ${reopening}` : ""}
                </span>
            }
        />
    );
};
